import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus } from "lucide-react";

export default function ManualActionDialog({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        type: "send_email",
        target: "",
        subject: "",
        body: "",
        execute: true,
    });

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.target.trim()) {
            toast.error("Target is required");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/actions", form);
            toast.success(
                `Action ${data.status} · ${data.provider || "logged"} · ${data.id.slice(0, 8)}`
            );
            setOpen(false);
            setForm({ type: "send_email", target: "", subject: "", body: "", execute: true });
            onCreated?.(data);
        } catch (err) {
            toast.error("Create failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="manual-create-btn"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-3 py-1.5 rounded-[2px]"
            >
                <Plus className="w-3 h-3" />
                New action
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="!bg-[#111315] !border !border-[#262a2e] !rounded-none !p-0 !max-w-lg"
                    data-testid="manual-create-dialog"
                >
                    <DialogHeader className="p-6 border-b border-[#262a2e]">
                        <DialogTitle className="font-sans text-lg font-semibold text-[#f3f4f6]">
                            New action
                        </DialogTitle>
                        <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                            log + execute a one-off action
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={submit} className="p-6 space-y-4">
                        <Field label="Type">
                            <select
                                value={form.type}
                                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                                data-testid="manual-type"
                                className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                            >
                                <option value="send_email">send_email</option>
                                <option value="followup">followup</option>
                                <option value="generate_content">generate_content</option>
                                <option value="wait">wait</option>
                            </select>
                        </Field>

                        <Field label="Target">
                            <input
                                value={form.target}
                                onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                                placeholder={
                                    form.type === "generate_content"
                                        ? "recipient@example.com (who the draft is for)"
                                        : "recipient@example.com"
                                }
                                data-testid="manual-target"
                                className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                                required
                            />
                        </Field>

                        {(form.type === "send_email" || form.type === "followup") && (
                            <>
                                <Field label="Subject">
                                    <input
                                        value={form.subject}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, subject: e.target.value }))
                                        }
                                        data-testid="manual-subject"
                                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                                    />
                                </Field>
                                <Field label="Body">
                                    <textarea
                                        value={form.body}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, body: e.target.value }))
                                        }
                                        rows={5}
                                        data-testid="manual-body"
                                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                                    />
                                </Field>
                            </>
                        )}

                        {form.type === "generate_content" && (
                            <Field label="Context">
                                <textarea
                                    value={form.body}
                                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                                    placeholder="What should the AI draft be about?"
                                    rows={4}
                                    data-testid="manual-body"
                                    className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                                />
                            </Field>
                        )}

                        <label className="flex items-center gap-2 font-mono text-[11px] text-[#9ca3af] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.execute}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, execute: e.target.checked }))
                                }
                                data-testid="manual-execute"
                                className="accent-[#7cc93c]"
                            />
                            execute immediately
                        </label>

                        <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#262a2e]">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9ca3af] px-3 py-1.5 border border-[#262a2e] rounded-[2px] hover:text-[#f3f4f6]"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="manual-submit"
                                className="font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-1.5 rounded-[2px] disabled:opacity-50"
                            >
                                {busy ? "Creating…" : form.execute ? "Create & run" : "Create"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}
